import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { DocumentDto } from '@/api/generated';
import {
  useDeleteDocument,
  useDocumentBlobUrl,
  useDocuments,
  useDownloadDocument,
  useUploadDocumentVersion,
} from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { ConfirmDialog } from '@/design-system/primitives/Dialog/ConfirmDialog';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { toast } from '@/design-system/primitives/Toast/Toast';
import { formatShortDate, toNumber } from '@/lib/format';
import styles from './DocPreview.module.css';
import { DocumentEditDialog } from './DocumentDialogs';

// 2026-05-17 — the static VERSIONS + ANNOTATIONS arrays were Phase-1
// design fixtures. The API doesn't model document versions as separate
// rows (Document.Version is a single integer field) and there's no
// annotation entity at all. Both removed from the SPA; the "Versies" and
// "Annotaties" cards no longer render. Reintroduce when the API surfaces
// a real version-history relation + annotation entity.

export function DocPreview() {
  const { id } = useParams<{ id: string }>();
  // The list endpoint is the only document read we expose in v1; locate the
  // record client-side and let TanStack Query reuse the cached page.
  const docsQuery = useDocuments({ pageSize: 50 });

  return (
    <AppFrame>
      <QueryBoundary query={docsQuery} loading="Document laden…">
        {(page) => {
          const doc = page.items.find((d) => d.id === id);
          if (!doc) {
            return (
              <div role="alert" className="mono c-bad">
                Document niet gevonden.{' '}
                <Link to="/documenten" className={styles.crumbLink}>
                  Terug naar overzicht
                </Link>
              </div>
            );
          }
          return <DocPreviewBody doc={doc} />;
        }}
      </QueryBoundary>
    </AppFrame>
  );
}

function DocPreviewBody({ doc }: { doc: DocumentDto }) {
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const download = useDownloadDocument();
  const deleteMutation = useDeleteDocument();
  const uploadVersion = useUploadDocumentVersion(doc.id);
  const versionInput = useRef<HTMLInputElement | null>(null);

  const onPickNewVersion = () => versionInput.current?.click();
  const onNewVersionChosen = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Always reset the input value so re-picking the same filename re-triggers onChange.
    event.target.value = '';
    if (!file) return;
    try {
      await uploadVersion.mutateAsync(file);
    } catch {
      // toast surfaced by the hook
    }
  };

  const onCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/documenten/${doc.id}`);
      toast.success('Link gekopieerd.');
    } catch (err) {
      toast.error(`Kon link niet kopiëren: ${err instanceof Error ? err.message : err}`);
    }
  };

  // Inline preview: load the doc's blob URL for PDFs (iframe) and images
  // (<img>). Other MIME types fall back to a download prompt — there is no
  // sensible in-browser viewer for, say, .docx without server-side rendering.
  const isPdf = doc.mime === 'application/pdf';
  const isImage = doc.mime.startsWith('image/');
  const canPreview = isPdf || isImage;
  const blobUrl = useDocumentBlobUrl(canPreview ? doc.id : undefined);

  return (
    <>
      <div className={`row gap-6 ${styles.crumb}`}>
        <Link to="/documenten" className={styles.crumbLink}>
          Documenten
        </Link>
        <Icon name="chevR" size={11} />
        <span>{doc.phaseId}</span>
        <Icon name="chevR" size={11} />
        <span className={styles.crumbCurrent}>{doc.name}</span>
      </div>

      <div className={styles.body}>
        <Card className={styles.previewCard}>
          <div className={`row gap-12 ${styles.previewHeader}`}>
            <div className={styles.fileIcon}>
              <Icon name="pdf" size={15} color="var(--bad)" />
            </div>
            <div className={`flex-1 ${styles.fileMeta}`}>
              <div className={styles.fileName}>{doc.name}</div>
              <div className={`mono ${styles.fileSub}`}>
                {formatBytes(doc.size)} · geüpload {formatShortDate(doc.uploadedAt)}
              </div>
            </div>
            <div className="row gap-4">
              <Btn
                variant="ghost"
                aria-label="Download"
                onClick={() => download.mutate({ id: doc.id, filename: doc.name })}
                disabled={download.isPending}
              >
                <Icon name="download" size={14} />
              </Btn>
              <Btn variant="ghost" aria-label="Link kopiëren" onClick={onCopyLink}>
                <Icon name="link" size={14} />
              </Btn>
              <Btn onClick={onPickNewVersion} disabled={uploadVersion.isPending}>
                <Icon name="upload" size={14} />{' '}
                {uploadVersion.isPending ? 'Uploaden…' : 'Nieuwe versie'}
              </Btn>
              <input
                ref={versionInput}
                type="file"
                style={{ display: 'none' }}
                onChange={onNewVersionChosen}
              />
              <Btn onClick={() => setEditOpen(true)}>
                <Icon name="edit" size={14} /> Bewerk
              </Btn>
              <Btn
                variant="ghost"
                className="c-bad"
                aria-label="Verwijder"
                onClick={() => setConfirmDelete(true)}
                disabled={deleteMutation.isPending}
              >
                <Icon name="trash" size={14} />
              </Btn>
            </div>
          </div>
          <DocumentEditDialog open={editOpen} onOpenChange={setEditOpen} doc={doc} />
          <ConfirmDialog
            open={confirmDelete}
            onOpenChange={setConfirmDelete}
            title={`${doc.name} verwijderen?`}
            description="Het document en het onderliggende bestand worden definitief verwijderd."
            confirmLabel="Verwijder"
            destructive
            pending={deleteMutation.isPending}
            onConfirm={async () => {
              try {
                await deleteMutation.mutateAsync(doc.id);
                setConfirmDelete(false);
                navigate('/documenten');
              } catch {
                // toast surfaced by the hook; keep dialog open so user can retry.
              }
            }}
          />

          <div className={`row gap-10 ${styles.toolbar}`}>
            <span className={`mono ${styles.toolbarMuted}`}>{doc.mime}</span>
            <div className="ml-auto row gap-6">
              <Pill kind="info" dot={false}>
                v{toNumber(doc.version)}
              </Pill>
            </div>
          </div>

          <div className={styles.previewArea}>
            {canPreview ? (
              blobUrl.isPending ? (
                <div className={`mono ${styles.previewState}`}>Preview laden…</div>
              ) : blobUrl.isError || !blobUrl.data ? (
                <div className={`mono ${styles.previewState} c-bad`}>
                  Preview niet beschikbaar — gebruik de Download-knop.
                </div>
              ) : isPdf ? (
                <iframe
                  src={blobUrl.data}
                  title={doc.name}
                  className={styles.previewFrame}
                />
              ) : (
                <img src={blobUrl.data} alt={doc.name} className={styles.previewImage} />
              )
            ) : (
              <div className={`col gap-12 ${styles.previewState}`}>
                <Icon name="paperclip" size={32} color="var(--ink-3)" />
                <span>Geen inline preview voor {doc.mime}.</span>
                <Btn
                  variant="primary"
                  onClick={() => download.mutate({ id: doc.id, filename: doc.name })}
                  disabled={download.isPending}
                >
                  <Icon name="download" size={14} /> Download {doc.name}
                </Btn>
              </div>
            )}
          </div>
        </Card>

        <div className={`col gap-16 ${styles.side}`}>
          {/* "Versies" + "Annotaties" cards removed 2026-05-17 — the API
              models neither today (Document.Version is a single integer,
              no separate version-history rows; annotations have no entity).
              The Eigenschappen card stays — it shows real fields from the
              uploaded Document. */}
          <Card className={styles.sideCard}>
            <h3 className={styles.sideCardTitle}>Eigenschappen</h3>
            <div className={`col gap-8 ${styles.propList}`}>
              <PropRow k="Fase" v={doc.phaseId} />
              <PropRow k="Versie" v={`v${toNumber(doc.version)}`} />
              <PropRow k="Bestand" v={doc.mime} />
              <PropRow k="Grootte" v={formatBytes(doc.size)} />
              <PropRow k="Geüpload" v={formatShortDate(doc.uploadedAt)} />
              <PropRow k="Uploader" v={doc.uploadedByDisplayName || doc.uploadedByClerkId} />
            </div>
            <div className="row gap-4" style={{ flexWrap: 'wrap', marginTop: 10 }}>
              {(doc.tags ?? []).length === 0 ? (
                <span className="mono c-ink-3" style={{ fontSize: 11 }}>
                  Geen tags — bewerk om er toe te voegen.
                </span>
              ) : (
                (doc.tags ?? []).map((t) => (
                  <Pill key={t} kind="idle" dot={false}>
                    {t}
                  </Pill>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

function PropRow({ k, v }: { k: string; v: string }) {
  return (
    <div className={`row gap-12 ${styles.propRow}`}>
      <span className={styles.propKey}>{k}</span>
      <span className={`flex-1 ${styles.propValue}`}>{v}</span>
    </div>
  );
}

function formatBytes(size: number | string): string {
  const n = toNumber(size);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
